import { readySpecialRepayments, type SpecialRepayments } from './specialRepayments';
import { centMoney, householdBudgetCents, householdEuros, readyHouseholdShare, type HouseholdShare } from './householdShare';
import { acquisitionBudgetCents, readyAcquisitionPayments, type AcquisitionPayments } from './acquisitionPayments';
import { readyDrawdown, upfrontLoanBridge, type Drawdown } from './loanDrawdown';
import { rentalTaxMonth, type RentalTax } from './rentalTax';
import defaultPlanning from '../../../../property-workflow/default-planning.json';
import feeTemplates from '../../../../property-workflow/acquisition-fees.json';
import type { Document } from './dueDiligence';
import { costEvidence, hasCostRange } from './costEvidence';
export type FeeKind =
  | 'transfer_tax'
  | 'notary'
  | 'registry'
  | 'broker'
  | 'finance'
  | 'other';
export const acquisitionFeeTemplates = feeTemplates;
export type SourceLevel =
  | 'unknown'
  | 'allowance'
  | 'estimate'
  | 'quote'
  | 'invoice';
export type QuoteComparison = { quoteRevisionId: string; scopeConfirmed: boolean };
export type CostRevision = {
  quoteComparison?: QuoteComparison;
  id: string;
  low: number | null;
  high: number | null;
  level: SourceLevel;
  documentId: string;
  locator: string;
  note: string;
  createdAt: string;
};
export type CostItem = {
  id: string;
  title: string;
  category: 'inspection' | 'repair' | 'purchase' | 'fees';
  dueMonth: number;
  revisions: CostRevision[];
  feeKind?: FeeKind;
};
export type ProjectTask = {
  id: string;
  title: string;
  owner: string;
  dueDate: string;
  status: 'todo' | 'waiting' | 'done';
  phase: 'assessment' | 'negotiation' | 'notary' | 'handover' | 'ownership';
  repeatMonths: number;
  reminderDays: number;
  documentId: string;
  note: string;
};
export type FinanceRequest = {
  id: string;
  lender: string;
  contact: string;
  status: 'draft' | 'requested' | 'documents_needed' | 'offered' | 'declined';
  dueDate: string;
  loanAmount: number | null;
  interestRate: number | null;
  repaymentRate: number | null;
  fixedYears: number | null;
  drawdown?: Drawdown;
  specialRepayments?: SpecialRepayments;
  refinanceInterestRate?: number | null;
  refinanceRepaymentRate?: number | null;
  effectiveRate: number | null;
  documentId: string;
  note: string;
  loanUse?: 'mixed' | 'private' | 'rental';
  useDocumentId?: string;
  useLocator?: string;
  useReviewed?: boolean;
  termSources?: Partial<
    Record<
      'loanAmount' | 'interestRate' | 'repaymentRate',
      { documentId: string; locator: string; quote: string; reviewedAt: string }
    >
  >;
};
export type UsageUnit = {
  id: string;
  title: string;
  areaM2: number;
  use: 'private' | 'rental';
  rentMonthly: number;
  rentalStartMonth: number;
  rentGrowthPercent: number;
  vacancyPercent: number;
  ownerCostsMonthly: number;
  maintenanceReserveMonthly: number;
  actualDeductibleCostsMonthly: number;
  legalRentabilityConfirmed: boolean;
  documentId: string;
  rentalTax?: RentalTax;
};
export type Planning = {
  costs: CostItem[];
  tasks: ProjectTask[];
  requests: FinanceRequest[];
  units: UsageUnit[];
  finance: {
    householdShare?: HouseholdShare;
    acquisitionPayments?: AcquisitionPayments;
    purchasePrice: number | null;
    purchaseFeesPercent: number;
    equity: number | null;
    retainedReserve: number;
    selectedRequestId: string;
    assumedInterestRate: number;
    assumedRepaymentRate: number;
    buildingValueSharePercent: number;
    completionYear: number | null;
    taxableIncomeBeforeRental: number | null;
    taxMode: 'marginal' | 'tariff2026';
    marginalTaxRate: number;
    jointAssessment: boolean;
    allowRentalLossOffset: boolean;
    interestAllocation: 'area' | 'documented';
    rentalInterestSharePercent: number;
    allocationDocumentId: string;
    horizonYears: number;
    feeMode?: 'allowance' | 'itemized';
    budgetReviewed?: boolean;
    financingMode?: 'single' | 'combined';
    selectedRequestIds?: string[];
  };
};
export const sourceLabels: Record<SourceLevel, string> = {
  unknown: 'Noch offen',
  allowance: 'Planungsreserve',
  estimate: 'Schätzung',
  quote: 'Angebot',
  invoice: 'Rechnung',
};
export const financeStatusLabels = {
  draft: 'Vorbereiten',
  requested: 'Angefragt',
  documents_needed: 'Unterlagen fehlen',
  offered: 'Angebot liegt vor',
  declined: 'Abgesagt',
};
export function updateFinanceRequest(
  planning: Planning,
  request: FinanceRequest,
): Planning {
  const previous = planning.requests.find((item) => item.id === request.id);
  const termSources = { ...request.termSources };
  for (const field of [
    'loanAmount',
    'interestRate',
    'repaymentRate',
  ] as const) {
    if (previous && previous[field] !== request[field])
      delete termSources[field];
  }
  const repaymentTermsChanged = previous && ['loanAmount', 'interestRate', 'repaymentRate', 'fixedYears', 'refinanceInterestRate', 'refinanceRepaymentRate', 'drawdown'].some(
    key => JSON.stringify(previous[key as keyof FinanceRequest]) !== JSON.stringify(request[key as keyof FinanceRequest]))
    || JSON.stringify(previous?.specialRepayments?.payments) !== JSON.stringify(request.specialRepayments?.payments);
  request = {
    ...request,
    ...(request.specialRepayments && repaymentTermsChanged ? { specialRepayments: { ...request.specialRepayments, contractConfirmed: false } } : {}),
    termSources,
    ...(previous && previous.loanAmount !== request.loanAmount
      ? { useReviewed: false }
      : {}),
  };
  const taskId = `finance:${request.id}`;
  const existing = planning.tasks.find((task) => task.id === taskId);
  const waiting = ['requested', 'documents_needed'].includes(request.status);
  const task: ProjectTask = {
    id: taskId,
    title: `Finanzierungsanfrage ${request.lender}: ${request.status === 'documents_needed' ? 'Unterlagen ergänzen' : 'Antwort nachfassen'}`,
    owner: existing?.owner ?? '',
    dueDate: request.dueDate,
    status: waiting ? 'waiting' : 'done',
    phase: 'negotiation',
    repeatMonths: 0,
    reminderDays: existing?.reminderDays ?? 1,
    documentId: request.documentId,
    note: request.note,
  };
  return {
    ...planning,
    requests: planning.requests.some((current) => current.id === request.id)
      ? planning.requests.map((current) =>
          current.id === request.id ? request : current,
        )
      : [...planning.requests, request],
    tasks: existing
      ? planning.tasks.map((current) =>
          current.id === taskId ? task : current,
        )
      : waiting
        ? [...planning.tasks, task]
        : planning.tasks,
  };
}
export function newPlanning(purchasePrice: number | null = null): Planning {
  const plan = structuredClone(defaultPlanning) as Planning;
  plan.finance.purchasePrice = purchasePrice;
  return plan;
}
export function costSummary(planning: Planning, documents: Document[] = []) {
  let low = 0;
  let high = 0;
  let unknown = 0;
  let documented = 0;
  const items = planning.costs.filter(
    (item) => item.category === 'inspection' || item.category === 'repair',
  );
  for (const item of items) {
    const revision = item.revisions.at(-1);
    if (!hasCostRange(revision)) {
      unknown++;
      continue;
    }
    low += revision.low;
    high += revision.high;
    if (costEvidence(item, documents) === 'linked')
      documented++;
  }
  return {
    low,
    high,
    unknown,
    documented,
    total: items.length,
    known: items.length - unknown,
    documentCoverage: items.length ? documented / items.length : 0,
  };
}
export function setFeeMode(
  planning: Planning,
  mode: 'allowance' | 'itemized',
): Planning {
  const missing =
    mode === 'itemized'
      ? feeTemplates.filter(
          (template) =>
            !planning.costs.some(
              (cost) =>
                cost.category === 'fees' && cost.feeKind === template.id,
            ),
        )
      : [];
  const usedIds = new Set(planning.costs.map((cost) => cost.id));
  const missingItems = missing.map((template) => {
    const base = `fee:${template.id}`;
    let id = base,
      suffix = 2;
    while (usedIds.has(id)) id = `${base}:${suffix++}`;
    usedIds.add(id);
    return {
      id,
      title: template.title,
      category: 'fees' as const,
      feeKind: template.id as FeeKind,
      dueMonth: 0,
      revisions: [],
    };
  });
  return {
    ...planning,
    finance: { ...planning.finance, feeMode: mode, budgetReviewed: false },
    costs: [...planning.costs, ...missingItems],
  };
}
export function acquisitionSummary(planning: Planning, documents: Document[] = []) {
  const mode =
    planning.finance.feeMode ??
    (planning.costs.some((cost) => cost.category === 'fees')
      ? 'itemized'
      : 'allowance');
  if (mode === 'allowance') {
    const total =
      ((planning.finance.purchasePrice ?? 0) *
        planning.finance.purchaseFeesPercent) /
      100;
    return {
      mode,
      low: total,
      high: total,
      depreciable: total,
      unknown: 0,
      documented: 0,
      total: 0,
      reserve: 0,
    };
  }
  let low = 0,
    high = 0,
    depreciable = 0,
    unknown = 0,
    documented = 0;
  const items = planning.costs.filter((cost) => cost.category === 'fees');
  for (const item of items) {
    const revision = item.revisions.at(-1);
    if (!hasCostRange(revision)) {
      unknown++;
      continue;
    }
    low += revision.low;
    high += revision.high;
    if (
      feeTemplates.find((template) => template.id === item.feeKind)?.depreciable
    )
      depreciable += revision.high;
    if (costEvidence(item, documents) === 'linked')
      documented++;
  }
  unknown += feeTemplates.filter(
    (template) => !items.some((cost) => cost.feeKind === template.id),
  ).length;
  const reserve = unknown
    ? Math.max(
        0,
        ((planning.finance.purchasePrice ?? 0) *
          planning.finance.purchaseFeesPercent) /
          100 -
          high,
      )
    : 0;
  return {
    mode,
    low,
    high: high + reserve,
    depreciable,
    unknown,
    documented,
    total: Math.max(items.length, feeTemplates.length),
    reserve,
  };
}
// EStG §32a, 2026. Input is taxable income, not gross salary. Future years
// deliberately keep this tariff as a planning assumption rather than predicting law.
export function germanIncomeTax2026(income: number, joint = false): number {
  if (joint) return 2 * germanIncomeTax2026(income / 2);
  const x = Math.floor(Math.max(0, income));
  const y = (x - 12348) / 10000;
  const z = (x - 17799) / 10000;
  return Math.max(
    0,
    Math.floor(
      x <= 12348
        ? 0
        : x <= 17799
          ? (914.51 * y + 1400) * y
          : x <= 69878
            ? (173.1 * z + 2397) * z + 1034.87
            : x <= 277825
              ? 0.42 * x - 11135.63
              : 0.45 * x - 19470.38,
    ),
  );
}
export function annualDepreciationRate(
  completionYear: number | null,
): number | null {
  return completionYear === null
    ? null
    : completionYear >= 2023
      ? 0.03
      : completionYear < 1925
        ? 0.025
        : 0.02;
}
export function financeProjection(planning: Planning) {
  const f = planning.finance;
  const costs = costSummary(planning);
  const householdFraction=readyHouseholdShare(f);
  const householdShareReady=householdFraction!==null;
  const scoped=(value:number)=>householdFraction?householdEuros(value,householdFraction):0;
  const warnings: string[] = [];
  const price = f.purchasePrice ?? 0;
  const acquisition = acquisitionSummary(planning);
  const fees = acquisition.high;
  const availableEquity = Math.max(0, (f.equity ?? 0) - f.retainedReserve);
  const selected = planning.requests.find(
    (request) => request.id === f.selectedRequestId,
  );
  const initialWorks = planning.costs
    .filter(
      (item) =>
        ['inspection', 'repair'].includes(item.category) && item.dueMonth < 12,
    )
    .reduce((sum, item) => sum + (item.revisions.at(-1)?.high ?? 0), 0);
  const acquisitionPayments = readyAcquisitionPayments(planning);
  const reviewedBudgetCents=acquisitionBudgetCents(planning);
  const wholeInitialBudget=price+fees+initialWorks;
  const scopedInitialBudget=f.householdShare===undefined?wholeInitialBudget:(householdBudgetCents(Math.round(wholeInitialBudget*100),f)??0)/100;
  const initialBudget=acquisitionPayments?acquisitionPayments.budgetCents/100:scopedInitialBudget;
  const requiredLoan = Math.max(0, initialBudget - availableEquity);
  const chosen =
    f.financingMode === 'combined'
      ? planning.requests.filter((request) =>
          f.selectedRequestIds?.includes(request.id),
        )
      : selected
        ? [selected]
        : [];
  const drawsReady = chosen.every(request => request.drawdown === undefined || readyDrawdown(request) !== null);
  const specialRepaymentsReady = chosen.every(request => request.specialRepayments === undefined || readySpecialRepayments(request) !== null);
  const debtReady = drawsReady && specialRepaymentsReady;
  const pendingSpecialRepaymentsCents = specialRepaymentsReady ? chosen.reduce((sum, request) => sum + (readySpecialRepayments(request)?.slice(f.horizonYears * 12).reduce((a, b) => a + b, 0) ?? 0), 0) : null;
  const initialBridgeCents = upfrontLoanBridge(planning);
  const paymentsReady = f.acquisitionPayments === undefined || acquisitionPayments !== null;
  const pendingAcquisitionPayments = acquisitionPayments?.paymentsCents.slice(f.horizonYears * 12).reduce((a,b) => a+b,0) ?? null;
  const loans = (chosen.length ? chosen : [null]).map((request) => {
    const amount =
      request?.loanAmount ??
      (f.financingMode === 'combined' ? 0 : requiredLoan);
    const rate = (request?.interestRate ?? f.assumedInterestRate) / 100;
    const repayment = (request?.repaymentRate ?? f.assumedRepaymentRate) / 100;
    const drawdown = request?.drawdown === undefined ? null : readyDrawdown(request);
    const staged = request?.drawdown !== undefined;
    return {
      staged, drawdown, specialRepayments: request ? readySpecialRepayments(request) : null, drawn: staged ? 0 : amount,
      id: request?.id ?? 'assumption',
      title: request?.lender ?? 'Planungsannahme',
      amount,
      rate,
      payment: (amount * (rate + repayment)) / 12,
      balance: staged ? 0 : amount,
      refinanceMonth:
        request?.fixedYears && request.fixedYears > 0
          ? Math.ceil(request.fixedYears * 12)
          : null,
      refinanceRate:
        request?.refinanceInterestRate == null
          ? null
          : request.refinanceInterestRate / 100,
      refinanceRepayment:
        request?.refinanceRepaymentRate == null
          ? null
          : request.refinanceRepaymentRate / 100,
      initialRepayment: repayment,
      remainingAtRefinance: null as number | null,
      use: request?.loanUse ?? 'mixed',
      evidenced: Boolean(
        request?.useReviewed && request.useDocumentId && request.useLocator,
      ),
    };
  });
  const loan = loans.reduce((sum, item) => sum + item.amount, 0);
  const plannedPayment = loans.reduce((sum, item) => sum + item.payment, 0);
  const totalArea = planning.units.reduce((sum, unit) => sum + unit.areaM2, 0);
  const depreciationRate = annualDepreciationRate(f.completionYear);
  const fundingGap = initialBudget - availableEquity - loan;
  const ownMoneyReady=f.householdShare===undefined||[f.purchasePrice,f.equity,f.retainedReserve,...chosen.map(r=>r.loanAmount)].every(centMoney);
  const depreciationBase=scoped(price+acquisition.depreciable);
  if (!householdShareReady) warnings.push('Haushaltsanteil noch offen: Bruchteil sowie einheitliche Rechte und die eigene Finanzierungsbelastung bestätigen. Mieten, Kosten, Steuer und vollständige Liquidität bleiben offen.');
  if (f.householdShare!==undefined) warnings.push('Manuelles Szenario mit einheitlichem wirtschaftlichem Anteil am gesamten Objekt. Kaufpreis, Objektkosten und Mietangaben bleiben Gesamtbeträge; Eigenkapital, Reserve, Darlehen, Abrufe und Budgetzahlungen gehören ausschließlich diesem Haushalt und werden nicht nochmals gekürzt. Abweichende Einheitsrechte, Rechtsnachweise, Anteilswechsel und zusätzliche gesamtschuldnerische Haftung sind nicht berechnet.');
  if (f.householdShare!==undefined&&(!ownMoneyReady||Math.abs(fundingGap)>0.005)) warnings.push('Die Haushaltsfinanzierung muss den anteiligen Anfangsbedarf centgenau decken. Ganze Centbeträge und eigene Darlehens-/Eigenkapitalbeträge prüfen; frühere Abruf- und Zahlungsbeträge werden nicht automatisch angepasst.');
  if (f.purchasePrice === null || f.equity === null)
    warnings.push(
      'Kaufpreis oder Eigenkapital fehlt; die Finanzierung ist noch unvollständig.',
    );
  if (costs.unknown)
    warnings.push(
      `${costs.unknown} Kostenpositionen sind unbewertet; die Summe ist nur ein bekannter Teilbetrag.`,
    );
  if (!costs.total && !f.budgetReviewed)
    warnings.push(
      'Prüfungen und Arbeiten sind noch nicht erfasst; ein leerer Kostenplan ist kein bestätigtes Null-Euro-Budget.',
    );
  if (!f.budgetReviewed)
    warnings.push(
      'Kostenumfang und Reserven sind noch nicht bewusst vollständig geprüft.',
    );
  if (acquisition.unknown)
    warnings.push(
      `${acquisition.unknown} Erwerbskosten sind noch offen; die Finanzierung enthält nur bekannte Teilbeträge.`,
    );
  if (acquisition.reserve)
    warnings.push(
      'Die Erwerbskosten enthalten noch eine unaufgeschlüsselte Planungsreserve aus der früheren Pauschale; sie ist keine belegte Kostenobergrenze.',
    );
  if (acquisition.mode === 'allowance')
    warnings.push(
      'Erwerbskosten sind eine pauschale Annahme. Einzelne Angebote und Bescheide können sie ersetzen.',
    );
  if (
    acquisition.mode === 'allowance' &&
    planning.costs.some((cost) => cost.category === 'fees')
  )
    warnings.push(
      'Vorhandene Einzelkosten werden im gewählten Pauschalmodus nicht zusätzlich gezählt.',
    );
  if (planning.costs.some((cost) => cost.category === 'purchase'))
    warnings.push(
      'Separate Kaufpreispositionen werden nicht addiert; den Gesamtpreis im Finanzierungsplan abstimmen.',
    );
  if (f.retainedReserve > (f.equity ?? 0))
    warnings.push(
      'Die gewünschte Reserve übersteigt das eingetragene Eigenkapital.',
    );
  if (fundingGap > 1)
    warnings.push(
      'Die ausgewählten Darlehen decken den bekannten Finanzierungsbedarf noch nicht.',
    );
  if (fundingGap < -1)
    warnings.push(
      'Die Darlehenssumme liegt über dem bekannten Bedarf. Mittelverwendung und Zinszuordnung prüfen.',
    );
  if (!chosen.length)
    warnings.push('Zins und Tilgung sind Planungsannahmen, kein Bankangebot.');
  if (chosen.some((request) => request.status !== 'offered'))
    warnings.push('Die gewählte Anfrage ist noch kein vorliegendes Angebot.');
  if (chosen.some((request) => !request.documentId))
    warnings.push('Zum gewählten Angebot fehlt ein Dokumentnachweis.');
  if (
    chosen.some((request) =>
      [request.loanAmount, request.interestRate, request.repaymentRate].some(
        (value) => value === null,
      ),
    )
  )
    warnings.push(
      'Im gewählten Angebot fehlen noch Betrag, Zins oder Tilgung; fehlende Werte bleiben Planungsannahmen.',
    );
  if (f.financingMode === 'combined' && !chosen.length)
    warnings.push(
      'Für eine kombinierte Finanzierung noch keine Bausteine ausgewählt.',
    );
  if (loans.some((item) => item.use !== 'mixed' && !item.evidenced))
    warnings.push(
      'Die private/vermietete Darlehenszuordnung ist unbelegt; für die Steuerplanung gilt vorläufig der Flächenschlüssel.',
    );
  if (
    planning.costs.some(
      (cost) =>
        cost.category === 'fees' &&
        ['finance', 'other'].includes(cost.feeKind ?? 'other'),
    )
  )
    warnings.push(
      'Finanzierungs- und weitere ungeprüfte Gebühren werden nicht automatisch als Gebäude-AfA oder sofortige Werbungskosten angesetzt.',
    );
  if (!totalArea)
    warnings.push('Flächen für private und vermietete Teile fehlen.');
  if (depreciationRate === null)
    warnings.push(
      'Fertigstellungsjahr fehlt; Gebäude-AfA wird noch nicht angesetzt.',
    );
  if (f.taxMode === 'tariff2026' && f.taxableIncomeBeforeRental === null)
    warnings.push(
      'Zu versteuerndes Einkommen fehlt; Steuerwirkung ist noch offen.',
    );
  if (f.interestAllocation === 'documented' && !f.allocationDocumentId)
    warnings.push(
      'Direkte Zinszuordnung ohne Beleg wird nicht übernommen; Flächenschlüssel als Planungsannahme.',
    );
  if (
    planning.units.some(
      (unit) => unit.use === 'rental' && !unit.legalRentabilityConfirmed,
    )
  )
    warnings.push(
      'Unbestätigte Vermietbarkeit: entsprechende Mietansätze werden nicht als gesicherte Einnahmen berücksichtigt.',
    );
  if (!drawsReady) warnings.push('Auszahlungsplan noch unvollständig oder widersprüchlich: Monatsbeträge, Gesamtsumme, Tilgungsbeginn und Bereitstellungsannahmen prüfen. Zinsen, Restschuld und Liquidität sind noch offen.');
  if (!specialRepaymentsReady) warnings.push('Sondertilgungen noch offen: Monate, Centbeträge und Zulässigkeit im jeweiligen Vertrag einschließlich Anschlussphase bestätigen. Zins-, Restschuld-, Steuer- und vollständige Liquiditätswerte bleiben offen.');
  if (chosen.some(request => request.specialRepayments !== undefined)) warnings.push('Sondertilgungen sind eigene Haushaltszahlungen am Monatsende nach der regulären Rate, frühestens ab dem vollständigen Abruf und Tilgungsbeginn. Sie senken die nächste Zinsbasis und die Restschuld, nicht die laufende Rate. Zahlungen werden auf die dann offene Schuld begrenzt; darüber hinaus geplantes Geld wird nicht ausgegeben. Vertragliche Höchstgrenzen, Gebühren und Ansprüche sind nicht geprüft.');
  if (loans.some(item => item.staged)) warnings.push('Abrufe sind manuelle Annahmen am Monatsanfang. Ohne separaten Zahlungsplan gelten Kauf und anfängliche Arbeiten als vollständig beim Kauf bezahlt; spätere Abrufe erstatten die Vorfinanzierung. Bereitstellungszinsen sind nur Liquiditätskosten, nicht automatisch steuerlich abziehbar.');
  if (!paymentsReady) warnings.push('Zahlungsplan noch offen oder widersprüchlich: vollständiges Anfangsbudget, centgenaue Gesamtfinanzierung und Abrufe nicht vor den zugehörigen Zahlungen prüfen. Liquidität bleibt offen.');
  if (f.acquisitionPayments !== undefined) warnings.push('Der manuelle Zahlungsplan verschiebt nur Zahlungen des gesamten Anfangsbudgets. Er bestätigt weder Eigentumsübergang, Mietberechtigung, AfA-Beginn noch steuerliche Abziehbarkeit. Die bisherigen Nutzungs- und Steuerannahmen gesondert prüfen.');
  if (pendingAcquisitionPayments) warnings.push('Zahlungen des Anfangsbudgets liegen nach dem Planende; der verbleibende Geldbestand muss diese noch finanzieren.');
  const monthlyRows: { specialRepayment: number; unappliedSpecialRepayment: number; acquisitionPayments: number; loanDrawReceipts: number; commitmentInterest: number; undrawnLoan: number; month: number; grossRent: number; debtService: number; cashCostsBeforeDebt: number;
    taxableRentalIncome: number | null; cashComplete: boolean; remainingLoan: number }[] = [];
  const rows: {
    year: number;
    specialRepayment: number;
    unappliedSpecialRepayment: number;
    acquisitionPayments: number;
    loanDrawReceipts: number;
    commitmentInterest: number;
    undrawnLoan: number;
    grossRent: number;
    rentalAreaShare: number;
    rentalCashCosts: number;
    reserve: number;
    interest: number;
    rentalInterest: number;
    depreciation: number;
    taxableRentalIncome: number | null;
    deductibleOperatingCosts: number | null;
    deductibleInterest: number | null;
    deductibleDepreciation: number | null;
    taxComplete: boolean;
    cashComplete: boolean;
    incomeTaxEffect: number | null;
    debtService: number;
    cashCostsBeforeDebt: number;
    beforeIncomeTaxOutflow: number;
    remainingLoan: number;
    refinances: string[];
    householdOutflow: number | null;
  }[] = [];
  for (
    let year = 1;
    year <= Math.max(1, Math.min(30, f.horizonYears));
    year++
  ) {
    let rent = 0;
    let cashCosts = 0;
    let reserve = 0;
    let totalCosts = 0;
    let interest = 0;
    let rentalInterest = 0;
    let depreciation = 0;
    let deductible = 0;
    let deductibleInterest = 0;
    let deductibleDepreciation = 0;
    let taxComplete = debtReady && householdShareReady;
    let cashComplete = debtReady && paymentsReady && householdShareReady;
    let loanDrawReceipts = 0;
    let commitmentInterest = 0;
    let debtService = 0;
    let specialRepayment = 0;
    let unappliedSpecialRepayment = 0;
    let areaShareSum = 0;
    const refinances: string[] = [];
    for (let month = (year - 1) * 12; month < year * 12; month++) {
      const before = { specialRepayment, unappliedSpecialRepayment, rent, totalCosts, debtService, deductible, deductibleInterest, deductibleDepreciation, loanDrawReceipts, commitmentInterest };
      const active = planning.units.filter(
        (unit) =>
          unit.use === 'rental' &&
          unit.legalRentabilityConfirmed &&
          month >= unit.rentalStartMonth,
      );
      const rentedArea = active.reduce((sum, unit) => sum + unit.areaM2, 0);
      const share = totalArea > 0 ? rentedArea / totalArea : 0;
      areaShareSum += share;
      for (const unit of planning.units) {
        totalCosts +=
          scoped(unit.ownerCostsMonthly) +
          scoped(unit.maintenanceReserveMonthly) +
          scoped(unit.actualDeductibleCostsMonthly);
      }
      const taxUnits = active.map(unit => ({ unit, tax: rentalTaxMonth(unit, month) }));
      const monthTaxComplete = debtReady && householdShareReady && !taxUnits.some(({tax}) => tax.factor === null)
        && !(active.length > 0 && (totalArea <= 0 || (f.buildingValueSharePercent > 0 && depreciationRate === null)));
      const monthCashComplete = debtReady && paymentsReady && householdShareReady && !taxUnits.some(({tax}) => tax.operating === null);
      taxComplete &&= monthTaxComplete;
      cashComplete &&= monthCashComplete;
      const taxArea = taxUnits.reduce((sum,{unit,tax}) => sum + unit.areaM2 * (tax.factor ?? 0),0);
      for (const {unit,tax} of taxUnits) {
        // Operating costs are paid in full; vacancy reduces tenant receipts,
        // leaving the owner with that cash cost. They are not a reserve.
        const operating = tax.operating ?? 0;
        rent += scoped((tax.cold + operating) * (1 - unit.vacancyPercent / 100));
        cashCosts += scoped(unit.ownerCostsMonthly) + scoped(unit.actualDeductibleCostsMonthly) + scoped(operating);
        totalCosts += scoped(operating);
        reserve += scoped(unit.maintenanceReserveMonthly);
        deductible += (scoped(unit.ownerCostsMonthly) + scoped(unit.actualDeductibleCostsMonthly) + scoped(operating)) * (tax.factor ?? 0);
      }
      const allocation =
        f.interestAllocation === 'documented' &&
        f.allocationDocumentId &&
        share > 0
          ? f.rentalInterestSharePercent / 100
          : share;
      for (const item of loans) {
        if (item.drawdown) {
          const draw = item.drawdown.stages.filter(stage => stage.month === month).reduce((sum, stage) => sum + Math.round(stage.amount * 100), 0) / 100;
          item.balance += draw; item.drawn = Math.round((item.drawn + draw) * 100) / 100;
          if (month > 0) loanDrawReceipts += draw;
          if (month >= item.drawdown.freeMonths) commitmentInterest += Math.max(0, item.amount - item.drawn) * item.drawdown.commitmentRate / 12;
        }
        if (month === item.refinanceMonth) {
          item.remainingAtRefinance = item.balance;
          if (item.balance > 0) {
            refinances.push(item.title);
            if (
              item.refinanceRate !== null ||
              item.refinanceRepayment !== null
            ) {
              item.rate = item.refinanceRate ?? item.rate;
              item.payment =
                (item.balance *
                  (item.rate +
                    (item.refinanceRepayment ?? item.initialRepayment))) /
                12;
            }
          }
        }
        const monthlyInterest = (item.balance * item.rate) / 12;
        const paid = Math.min(item.balance + monthlyInterest, item.staged && (!item.drawdown || month < item.drawdown.repaymentStartMonth) ? monthlyInterest : item.payment);
        interest += monthlyInterest;
        debtService += paid;
        item.balance = Math.max(0, item.balance + monthlyInterest - paid);
        const scheduled = (item.specialRepayments?.[month] ?? 0) / 100;
        const extra = Math.min(item.balance, scheduled);
        item.balance = Math.max(0, item.balance - extra);
        specialRepayment += extra;
        unappliedSpecialRepayment += scheduled - extra;
        debtService += extra;
        const assigned =
          item.evidenced && item.use === 'private'
            ? 0
            : item.evidenced && item.use === 'rental'
              ? share > 0
                ? 1
                : 0
              : allocation;
        rentalInterest += monthlyInterest * assigned;
        // Existing loan attribution selects the rental pool; this model uses
        // active rental area to allocate that pool across its units.
        deductibleInterest += monthlyInterest * assigned * (rentedArea > 0 ? taxArea / rentedArea : 0);
      }
      depreciation +=
        (((depreciationBase * f.buildingValueSharePercent) /
          100) *
          share *
          (depreciationRate ?? 0)) /
        12;
      deductibleDepreciation += (((depreciationBase * f.buildingValueSharePercent) / 100)
        * (totalArea > 0 ? taxArea / totalArea : 0) * (depreciationRate ?? 0)) / 12;
      // First-year works are already in acquisition funding. Later works fall
      // in their entered month; never spread these across the rest of a year.
      const work = planning.costs.filter(item => ['inspection', 'repair'].includes(item.category)
        && item.dueMonth >= 12 && item.dueMonth === month)
        .reduce((sum, item) => sum + scoped(item.revisions.at(-1)?.high ?? 0), 0);
      monthlyRows.push({ month, specialRepayment: specialRepayment - before.specialRepayment, unappliedSpecialRepayment: unappliedSpecialRepayment - before.unappliedSpecialRepayment, acquisitionPayments: month > 0 ? (acquisitionPayments?.paymentsCents[month] ?? 0) / 100 : 0, loanDrawReceipts: loanDrawReceipts - before.loanDrawReceipts, commitmentInterest: commitmentInterest - before.commitmentInterest, undrawnLoan: loans.reduce((sum, item) => sum + Math.max(0, item.amount - item.drawn), 0), grossRent: rent - before.rent, debtService: debtService - before.debtService,
        cashCostsBeforeDebt: totalCosts - before.totalCosts + work + commitmentInterest - before.commitmentInterest,
        taxableRentalIncome: monthTaxComplete ? rent - before.rent - (deductible - before.deductible)
          - (deductibleInterest - before.deductibleInterest) - (deductibleDepreciation - before.deductibleDepreciation) : null,
        cashComplete: monthCashComplete, remainingLoan: loans.reduce((sum, item) => sum + item.balance, 0) });
    }
    const taxableRentalIncome = taxComplete
      ? rent - deductible - deductibleInterest - deductibleDepreciation : null;
    const taxableChange = taxableRentalIncome === null ? null : f.allowRentalLossOffset
      ? taxableRentalIncome
      : Math.max(0, taxableRentalIncome);
    const incomeTaxEffect = taxableChange === null ? null : taxableChange === 0 ? 0 :
      f.taxMode === 'tariff2026'
        ? f.taxableIncomeBeforeRental === null
          ? null
          : germanIncomeTax2026(
              f.taxableIncomeBeforeRental + taxableChange,
              f.jointAssessment,
            ) -
            germanIncomeTax2026(f.taxableIncomeBeforeRental, f.jointAssessment)
        : (taxableChange * f.marginalTaxRate) / 100;
    // Initial work is already included in acquisition funding. Future work must
    // be funded from later liquidity and must not be silently borrowed today.
    const oneOffCost = planning.costs
      .filter(
        (item) =>
          ['inspection', 'repair'].includes(item.category) &&
          item.dueMonth >= 12 &&
          Math.floor(item.dueMonth / 12) === year - 1,
      )
      .reduce((sum, item) => sum + scoped(item.revisions.at(-1)?.high ?? 0), 0);
    const laterPayments = monthlyRows.slice((year - 1) * 12, year * 12).reduce((sum, row) => sum + row.acquisitionPayments, 0);
    rows.push({
      year, specialRepayment, unappliedSpecialRepayment, acquisitionPayments: laterPayments,
      loanDrawReceipts, commitmentInterest, undrawnLoan: loans.reduce((sum, item) => sum + Math.max(0, item.amount - item.drawn), 0),
      grossRent: rent,
      rentalAreaShare: areaShareSum / 12,
      rentalCashCosts: cashCosts,
      reserve,
      interest,
      rentalInterest,
      depreciation,
      taxableRentalIncome,
      deductibleOperatingCosts: taxComplete ? deductible : null,
      deductibleInterest: taxComplete ? deductibleInterest : null,
      deductibleDepreciation: taxComplete ? deductibleDepreciation : null,
      taxComplete,
      cashComplete,
      incomeTaxEffect,
      debtService,
      cashCostsBeforeDebt: totalCosts + oneOffCost + commitmentInterest,
      beforeIncomeTaxOutflow: debtService + totalCosts + oneOffCost + commitmentInterest + laterPayments - rent - loanDrawReceipts,
      remainingLoan: loans.reduce((sum, item) => sum + item.balance, 0),
      refinances,
      householdOutflow:
        incomeTaxEffect === null || !cashComplete
          ? null
          : debtService + totalCosts + oneOffCost + commitmentInterest + laterPayments - rent - loanDrawReceipts + incomeTaxEffect,
    });
  }
  if (debtReady && householdShareReady && rows.some(row => !row.taxComplete)) warnings.push('Warmmietvergleich oder erforderliche Totalüberschussprognose noch offen; die betroffenen Steuer- und Nachsteuerwerte bleiben offen.');
  if (debtReady && paymentsReady && householdShareReady && rows.some(row => !row.cashComplete)) warnings.push('Umlagefähige Betriebskosten noch offen; Miet- und Kostenwerte zeigen bekannte Teilbeträge.');
  if (planning.units.filter(unit => unit.use === 'rental').length > 1) warnings.push('Zinsen des Mietanteils werden innerhalb der Mietwohnungen nach aktiver Fläche verteilt. Abweichende direkte Wohnungszuordnung gesondert prüfen.');
  let futureTermsMissing = false;
  for (const request of chosen) {
    if (!request.fixedYears || request.fixedYears <= 0) {
      warnings.push(
        `${request.lender}: Ende der Zinsbindung fehlt. Die Fortschreibung ist eine Planungsannahme für den gesamten Zeitraum.`,
      );
      futureTermsMissing = true;
    } else if (
      f.horizonYears > request.fixedYears &&
      (loans.find((item) => item.id === request.id)?.remainingAtRefinance ??
        0) > 0
    ) {
      if (
        request.refinanceInterestRate == null ||
        request.refinanceRepaymentRate == null
      ) {
        futureTermsMissing = true;
        warnings.push(
          `${request.lender}: Anschlusszins oder Tilgung nach ${request.fixedYears} Jahren fehlt. Fehlende Angaben bleiben auf dem bisherigen Niveau; bei einem teilweise erfassten Anschlussplan wird die Rate auf die Restschuld neu angesetzt.`,
        );
      }
      warnings.push(
        `${request.lender}: Die Anschlussfinanzierung nach ${request.fixedYears} Jahren ist ein Szenario, kein zugesagtes Bankangebot. Weitere Änderungen sind im Plan nicht berücksichtigt.`,
      );
    }
  }
  return {
    price,
    fees,
    acquisition,
    loans,
    loan,
    payment: debtReady && householdShareReady ? monthlyRows[0]?.debtService ?? plannedPayment : null,
    householdShareReady, householdFraction, ownMoneyReady, reviewedBudgetCents, initialBudget, wholeInitialBudget,
    drawsReady, debtReady, specialRepaymentsReady, pendingSpecialRepaymentsCents, initialBridgeCents, paymentsReady, acquisitionPayments, pendingAcquisitionPayments,
    fundingGap,
    availableEquity,
    costs,
    warnings,
    rows,
    monthlyRows,
    complete:
      debtReady && paymentsReady && householdShareReady && ownMoneyReady && (f.householdShare===undefined||Math.abs(fundingGap)<0.005) &&
      f.purchasePrice !== null &&
      f.equity !== null &&
      costs.unknown === 0 &&
      acquisition.unknown === 0 &&
      Boolean(f.budgetReviewed) &&
      !futureTermsMissing &&
      (f.financingMode !== 'combined' || chosen.length > 0) &&
      !chosen.some((request) =>
        [request.loanAmount, request.interestRate, request.repaymentRate].some(
          (value) => value === null,
        ),
      ),
  };
}
export function addMonths(date: string, months: number): string {
  const original = new Date(`${date}T12:00:00Z`);
  if (Number.isNaN(original.getTime())) return '';
  const result = new Date(
    Date.UTC(original.getUTCFullYear(), original.getUTCMonth() + months, 1, 12),
  );
  result.setUTCDate(
    Math.min(
      original.getUTCDate(),
      new Date(
        Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0),
      ).getUTCDate(),
    ),
  );
  return result.toISOString().slice(0, 10);
}
export function taskCalendar(
  tasks: ProjectTask[],
  propertyTitle: string,
): string {
  const escape = (value: string) =>
    value
      .replaceAll('\\', '\\\\')
      .replace(/\r\n|\r|\n/g, '\\n')
      .replaceAll(',', '\\,')
      .replaceAll(';', '\\;');
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Property Workspace//Tasks//DE',
  ];
  for (const task of tasks.filter(
    (task) =>
      task.status !== 'done' && /^\d{4}-\d{2}-\d{2}$/.test(task.dueDate),
  )) {
    lines.push(
      'BEGIN:VEVENT',
      `UID:${encodeURIComponent(task.id)}@property-workspace`,
      `DTSTAMP:${new Date()
        .toISOString()
        .replace(/[-:]/g, '')
        .replace(/\.\d{3}/, '')}`,
      `DTSTART;VALUE=DATE:${task.dueDate.replaceAll('-', '')}`,
      `SUMMARY:${escape(`${propertyTitle}: ${task.title}`)}`,
      `DESCRIPTION:${escape(`${task.owner}\n${task.note}`)}`,
    );
    if (task.repeatMonths > 0) {
      const day = Number(task.dueDate.slice(-2));
      lines.push(
        `RRULE:FREQ=MONTHLY;INTERVAL=${task.repeatMonths}${day >= 29 ? `;BYMONTHDAY=${day},-1;BYSETPOS=1` : ''}`,
      );
    }
    if (task.reminderDays >= 0)
      lines.push(
        'BEGIN:VALARM',
        `TRIGGER:-P${task.reminderDays}D`,
        'ACTION:DISPLAY',
        `DESCRIPTION:${escape(task.title)}`,
        'END:VALARM',
      );
    lines.push('END:VEVENT');
  }
  lines.push('END:VCALENDAR');
  return (
    lines
      .map((line) => {
        let folded = '';
        let bytes = 0;
        const encoder = new TextEncoder();
        for (const char of line) {
          const length = encoder.encode(char).length;
          if (bytes + length > 75) {
            folded += '\r\n ';
            bytes = 1;
          }
          folded += char;
          bytes += length;
        }
        return folded;
      })
      .join('\r\n') + '\r\n'
  );
}
